import type { FieldState } from 'final-form';
import { get, includes, isNil } from 'lodash';
import moment from 'moment';
import { parseContactNumber, parseLandlineNumber, parseMobileNumber } from '@lendi/core-mobilenormalizer';

import { FIELD_PATHS } from '@funnels/constants';
import { Address, Owner, StoreApplicant } from '@funnels/types';
import { hasPartialOwnershipByApplicantType } from '@funnels/utils';

import isBlank from './isBlank';
import validateDate from './validateDate';

///////////////////////////////
// FINAL FIELD COMMON CONFIG //
///////////////////////////////

export function shouldSkipValidate(
  value: any,
  isOptional?: boolean,
  allValues?: Object,
  fieldMeta?: FieldState<any>
): boolean {
  const isValueBlank = isBlank(value);

  return (
    (isOptional === true && isValueBlank) ||
    get(allValues, FIELD_PATHS.TEMP_SKIP_VALIDATION_FIELDS(), [] as string[]).includes(fieldMeta?.name ?? '')
  );
}

// Validating
export function validateNumber(validationFailMessage: string = 'Please enter a number.', isOptional: boolean = false) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    const isValid = value !== null && !isNaN(value);
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || isValid) {
      return;
    }
    return validationFailMessage;
  };
}

export function validatePositiveNumber(
  validationFailMessage: string = 'Please enter a number greater or equal to 0.',
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    const isValid = value !== null && !isNaN(value) && value >= 0;
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || isValid) {
      return;
    }
    return validationFailMessage;
  };
}

export function validatePositiveNonZeroNumber(
  validationFailMessage: string = 'Please enter a number greater than 0.',
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    const isValid = value !== null && !isNaN(value) && value > 0;
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || isValid) {
      return;
    }
    return validationFailMessage;
  };
}

export function validateInclusiveNumberRanges(
  minValue: number,
  maxValue: number,
  isOptional: boolean = false,
  validationFailMessage?: string,
  validationEmptyMessage?: string
) {
  validationFailMessage = validationFailMessage
    ? validationFailMessage
    : `Please enter a number between ${minValue} and ${maxValue}.`;
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    const isValid = value !== null && !isNaN(value) && value >= minValue && value <= maxValue;

    if (validationEmptyMessage && isBlank(value) && isOptional === false) {
      return validationEmptyMessage;
    }

    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || isValid) {
      return;
    }
    return validationFailMessage;
  };
}

export function validateExclusiveNumberRanges(
  minValue: number,
  maxValue: number,
  isOptional: boolean = false,
  validationFailMessage?: string
) {
  validationFailMessage = validationFailMessage
    ? validationFailMessage
    : `Please enter a number above ${minValue} and under ${maxValue}.`;
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    const isValid = value !== null && !isNaN(value) && value > minValue && value < maxValue;
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || isValid) {
      return;
    }
    return validationFailMessage;
  };
}

export function validateNotUndefined(
  validationFailMessage: string = 'Please select a value.',
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    const isValid = value !== null && value !== undefined;
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || isValid) {
      return;
    }
    return validationFailMessage;
  };
}

export function validateNotEmpty(
  validationFailMessage: string = 'Please select a value.',
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    const isValid = Array.isArray(value) ? !isBlank(value) && value.some((v) => !isBlank(v)) : !isBlank(value);
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || isValid) {
      return;
    }
    return validationFailMessage;
  };
}

export function validateNotEmptyAutoCompleteRoleValue(
  validationFailMessage: string = 'Please select a value.',
  isOptional: boolean = false,
  dataSource: any[] = []
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    const isValid = value?.anzsco?.code && dataSource.find((data) => data.value === value.anzsco.code);
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || isValid) {
      return;
    }
    return validationFailMessage;
  };
}

export function validateNotEmptyString(
  validationFailMessage: string = 'Please enter a value.',
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    const isValid = value !== null && value !== undefined && value !== '';
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || isValid) {
      return;
    }
    return validationFailMessage;
  };
}

export function validateFixedLength(
  validationFailMessage: string = 'Please enter a valid value.',
  fixedLength: number,
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || String(value).length === fixedLength) {
      return;
    }

    return validationFailMessage;
  };
}

export function validateDateString(
  validationFailMessage: string = 'Date value is incorrect.',
  isOptional: boolean = false,
  hasDayField: boolean = false,
  minYearDiff?: number,
  minDate?: string,
  maxDate?: string
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta)) {
      return;
    }

    if (!validateDate(value)) {
      return validationFailMessage;
    }

    const dateValue = moment(value, [moment.HTML5_FMT.DATE, moment.HTML5_FMT.DATETIME_LOCAL_MS], true);
    const minYear = moment().startOf('year').subtract(115, 'years');
    const dateValueMS = Date.parse(value);
    if (!dateValue.isValid()) {
      return validationFailMessage;
    }
    if (dateValue.isBefore(minYear)) {
      return validationFailMessage;
    }
    if (minYearDiff && Math.abs(moment.duration(dateValue.diff(moment())).years()) <= minYearDiff) {
      return validationFailMessage;
    }

    if (minDate && validateDate(minDate)) {
      const minDateValueMS = Date.parse(minDate);
      if (minDateValueMS < dateValueMS) {
        return `Please enter a date later than ${minDate}`;
      }
    }

    if (maxDate && validateDate(maxDate)) {
      const maxDateValueMS = Date.parse(maxDate);
      if (dateValueMS > maxDateValueMS) {
        return `Please enter a date prior than ${maxDate}`;
      }
    }

    return;
  };
}

export function validateNotFutureDate(
  validationFailMessage: string = 'Date cannot be in the future, must be present or past date',
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta)) {
      return;
    }

    return validateDateString()(value) || moment(value).isAfter(moment())
      ? validateDateString()(value) || validationFailMessage
      : undefined;
  };
}

export function validateSameOrFutureMonth(
  validationFailMessage: string = 'Please enter a future date on or after this month',
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta)) {
      return;
    }

    return validateDateString()(value) || moment(value).isBefore(moment(), 'month')
      ? validateDateString()(value) || validationFailMessage
      : undefined;
  };
}

export function validateDateAfter(
  date: string | undefined,
  validationFailMessage: string = 'Please enter a date later than the provided date',
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (!date || !moment(date).isValid() || shouldSkipValidate(value, isOptional, allValues, fieldMeta)) {
      return;
    }

    return validateDateString()(value) || moment(value) <= moment(date)
      ? validateDateString()(value) || validationFailMessage
      : undefined;
  };
}

// eslint-disable-next-line
const VALID_EMAIL_REGEX =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function validateEmail(
  validationFailMessage: string = 'Please enter a valid value.',
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || VALID_EMAIL_REGEX.test(value)) {
      return;
    }

    return validationFailMessage;
  };
}

export function parsePhoneNumber(value: any) {
  if (value === '') {
    return undefined;
  }

  value = value
    .replace(/[^0-9]/gi, '')
    .replace(/^0/g, '')
    .slice(0, 9);

  return `+61${value}`;
}

export function formatPostCode(value: any) {
  const postcode = value ? value.replace(/[^0-9]/gi, '') : '';

  if (postcode.length > 4) {
    return postcode.slice(0, 4);
  }

  return postcode;
}

export function formatPhoneNumber(value: any) {
  if (isNil(value)) {
    return '';
  }
  value = value.replace(/^(0061|\+61)/, '').replace(/[^0-9]/gi, '');

  if (value[0] !== '0') {
    value = `0${value}`;
  }

  return [...value].reduce((state, el, index) => {
    state += el;

    if (includes([3, 6], index) && index + 1 < value.length) {
      state += ` `;
    }

    return state;
  }, '');
}

export function validateUnderEighteen(
  validationFailMessage: string = 'Please enter a valid number under 18',
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (
      shouldSkipValidate(value, isOptional, allValues, fieldMeta) ||
      (value !== null && value !== undefined && value < 18)
    ) {
      return;
    }
    return validationFailMessage;
  };
}

export function validateAddress(
  validationFailMessage: string = 'Please enter a correct address.',
  addressObj: Address,
  isOptional: boolean = false,
  allowOverseaAddress: boolean = true,
  allowPOBox: boolean = false,
  allowBaseOnlyValidation: boolean = false
) {
  return (value: any) => {
    const {
      country = 'Australia',
      postcode,
      state,
      city,
      standard: { streetName, streetNumber, lotSection, deliveryAddress } = {},
    } = addressObj;
    const isBaseAddressValid =
      postcode && state && city && (allowOverseaAddress || (!allowOverseaAddress && country === 'Australia'));
    const isFullAddressValid = isBaseAddressValid && streetName && (streetNumber || lotSection);
    const isResidentialAddressValid = allowBaseOnlyValidation ? isBaseAddressValid : isFullAddressValid;
    const isPOBoxAddressValid = allowPOBox && deliveryAddress && isBaseAddressValid;

    if (shouldSkipValidate(value, isOptional) || isResidentialAddressValid || isPOBoxAddressValid) {
      return;
    }
    return validationFailMessage;
  };
}

export function validatePostCode(
  validationFailMessage: string = 'Please enter a valid postcode',
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    const ausPostCodeRex =
      /^(0[289][0-9]{2})$|^([1345689][0-9]{3})$|^(2[0-8][0-9]{2})$|^(290[0-9])$|^(291[0-4])$|^(7[0-4][0-9]{2})$|^(7[8-9][0-9]{2})$/;

    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || ausPostCodeRex.test(value)) {
      return;
    }

    return validationFailMessage;
  };
}

export function validateOwnershipPercentsEqualsOneAndPartialOwnership(
  validationFailMessage?: string,
  isOptional?: boolean,
  targetApplicantType?: string
) {
  return (value?: Owner[], allValues?: Object, fieldMeta?: FieldState<any>) =>
    shouldSkipValidate(value, isOptional, allValues, fieldMeta) ||
    (targetApplicantType
      ? validatePartialOwnershipByApplicantType(validationFailMessage!, isOptional, targetApplicantType)(
          value,
          allValues,
          fieldMeta
        )
      : undefined) ||
    validateOwnershipPercentsEqualsOne(undefined, isOptional, undefined)(value, allValues, fieldMeta);
}

export function validateOwnershipPercentsEqualsOne(
  validationFailMessage: string = 'Ownership percentage must amount to 100%',
  isOptional: boolean = false,
  canAllowZeroPercent: boolean = false
) {
  return (value?: Owner[], allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (
      shouldSkipValidate(value, isOptional, allValues, fieldMeta) ||
      (Array.isArray(value) &&
        value.reduce((a: number, b: Owner) => a + b.percent, 0) === 1 &&
        (canAllowZeroPercent || !value.some((val) => val.percent === 0)))
    ) {
      return;
    }

    return validationFailMessage;
  };
}

export function validateOwnershipPercentsGreaterThanZeroAndLessThanOrEqualToOneAndPartialOwnership(
  validationFailMessage?: string,
  isOptional?: boolean,
  targetApplicantType?: string
) {
  return (value?: Owner[], allValues?: Object, fieldMeta?: FieldState<any>) =>
    shouldSkipValidate(value, isOptional, allValues, fieldMeta) ||
    (targetApplicantType
      ? validatePartialOwnershipByApplicantType(validationFailMessage!, isOptional, targetApplicantType)(
          value,
          allValues,
          fieldMeta
        )
      : undefined) ||
    validateOwnershipPercentsGreaterThanZeroAndLessThanOrEqualToOne(undefined, isOptional)(value, allValues, fieldMeta);
}

export function validateOwnershipPercentsGreaterThanZeroAndLessThanOrEqualToOne(
  validationFailMessage: string = 'Ownership percentage must amount to 100% and include all applicants',
  isOptional: boolean = false
) {
  return (value?: Owner[], allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (
      shouldSkipValidate(value, isOptional, allValues, fieldMeta) ||
      (Array.isArray(value) &&
        value.reduce((a: number, b: Owner) => a + b.percent, 0) <= 1 &&
        value.reduce((a: number, b: Owner) => a + b.percent, 0) > 0 &&
        !value.some((val) => val.percent === 0))
    ) {
      return;
    }
    return validationFailMessage;
  };
}

export function validatePartialOwnershipByApplicantType(
  validationFailMessage: string,
  isOptional: boolean = false,
  targetApplicantType?: string
) {
  return (value?: Owner[], allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (
      shouldSkipValidate(value, isOptional, allValues, fieldMeta) ||
      (Array.isArray(value) &&
        (!targetApplicantType || hasPartialOwnershipByApplicantType(allValues!, value, targetApplicantType)))
    ) {
      return;
    }
    return validationFailMessage;
  };
}

export function validateNumberLessThanOrEqualTo(
  validationFailMessage: string,
  maxValue: number,
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || value <= maxValue) {
      return;
    }
    return validationFailMessage;
  };
}

export function validateNumberLessThan(validationFailMessage: string, maxValue: number, isOptional: boolean = false) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || value < maxValue) {
      return;
    }
    return validationFailMessage;
  };
}

export function validateNumberGreaterThan(
  validationFailMessage: string,
  minValue: number,
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || value > minValue) {
      return;
    }
    return validationFailMessage;
  };
}

export function validateOptionIsOneOf(
  validationFailMessage: string,
  validOptions: string[],
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || validOptions.includes(value)) {
      return;
    }
    return validationFailMessage;
  };
}

export function validMobileNumberUniqueness(
  currentApplicantIndex: number,
  applicants: StoreApplicant[],
  validationFailMessageForUniqueness: string = 'Applicants cannot have the same mobile number. Please enter a different mobile number.'
) {
  return (value: any) => {
    for (let i = 0; i < applicants.length; i++) {
      const mobile = get({ applicants }, FIELD_PATHS.APPLICANT_MOBILE(i));
      if (mobile === value && i !== currentApplicantIndex) {
        return validationFailMessageForUniqueness;
      }
    }
    return;
  };
}

export function validateCreditLimitGreaterThanOrEqualToCurrentBalance(
  validationFailMessage: string,
  currentBalance: number,
  isOptional?: boolean
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    const isValid = value !== null && !isNaN(value) && value >= 0;

    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || (isValid && value >= currentBalance)) {
      return;
    }

    return validationFailMessage;
  };
}

export function validateMaternityReturnDate() {
  return (value: any) => {
    const errors = validateDateString()(value);
    if (errors) {
      return errors;
    } else {
      const valueMs = new Date(value).getTime();
      const now = new Date().getTime();
      if (valueMs <= now) {
        return 'Please enter a return date after this month';
      }
    }
    return;
  };
}

export function validateBlacklistedSingleValue(
  blacklistedSingleValue: string,
  validationFailMessage: string,
  // value for dropdown is always an object
  comparisonPath: string = '',
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta)) {
      return;
    }
    // only consider array case since it targets at dropdown
    if (
      Array.isArray(value) &&
      value.length === 1 &&
      `${get(value[0], comparisonPath, value[0])}` === blacklistedSingleValue
    ) {
      return validationFailMessage;
    }

    return;
  };
}

export function validatePhoneNumber(
  validationFailMessage: string,
  target: 'mobile' | 'landline' | 'all',
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta)) {
      return;
    }
    try {
      if (target === 'mobile') {
        return parseMobileNumber(value).valid ? undefined : validationFailMessage;
      }
      if (target === 'landline') {
        return parseLandlineNumber(value).valid ? undefined : validationFailMessage;
      }
      return parseContactNumber(value).valid ? undefined : validationFailMessage;
    } catch (err) {
      return validationFailMessage;
    }
  };
}

export function validateVehicleYear(
  validationFailMessage: string = 'The model year must be above 1900.',
  isOptional: boolean = false
) {
  return (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    const isValid = value !== null && !isNaN(value) && value > 1900;
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta) || isValid) {
      return;
    }
    return validationFailMessage;
  };
}

/**
 * Formats the year and removes any character after the 4th one, returns the number
 * @param year
 */
export function formatYear(year: string) {
  if (year.length > 4) {
    return year.slice(0, 4);
  }

  return parseInt(year);
}
